import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, FileText, Shield, Check } from 'lucide-react';
import { useAuth, getAuthHeaders } from '../context/AuthContext';

import { BACKEND_URL } from '../config/api';

const TermsAcceptanceModal = ({ onAccepted }) => {
  const { logout } = useAuth();
  const [tosChecked, setTosChecked] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const canAccept = tosChecked && privacyChecked && !submitting;

  const handleAccept = async () => {
    if (!canAccept) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/accept-terms`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        onAccepted();
      }
    } catch {
      // Retry silently
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    await logout();
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4" data-testid="terms-acceptance-screen">
      <div className="bg-white rounded-2xl border border-beige shadow-lg max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="bg-sage/5 border-b border-beige px-6 py-5 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Sparkles className="h-6 w-6 text-sage" />
            <span className="text-xl font-light text-charcoal">Kura Stash</span>
          </div>
          <h2 className="text-lg font-medium text-charcoal">Welcome! One quick step</h2>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          <p className="text-sm text-charcoal/80 leading-relaxed mb-6">
            To enjoy the full benefits of Kura Stash — organizing your craft supplies,
            getting AI-powered design ideas, and more — please review and agree to our
            Terms of Service and Privacy Policy.
          </p>

          {/* Checkboxes */}
          <div className="space-y-4 mb-6">
            <label
              className="flex items-start gap-3 cursor-pointer group"
              data-testid="tos-checkbox-label"
            >
              <div className="relative mt-0.5">
                <input
                  type="checkbox"
                  checked={tosChecked}
                  onChange={() => setTosChecked(!tosChecked)}
                  className="sr-only"
                  data-testid="tos-checkbox"
                />
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 ${
                  tosChecked
                    ? 'bg-sage border-sage'
                    : 'border-gray-300 group-hover:border-sage/50'
                }`}>
                  {tosChecked && <Check size={14} className="text-white" strokeWidth={3} />}
                </div>
              </div>
              <span className="text-sm text-charcoal/80 leading-relaxed">
                I have read and agree to the{' '}
                <Link
                  to="/terms"
                  target="_blank"
                  className="text-sage hover:text-sage-dark underline font-medium"
                  data-testid="tos-link"
                >
                  Terms of Service
                </Link>
              </span>
            </label>

            <label
              className="flex items-start gap-3 cursor-pointer group"
              data-testid="privacy-checkbox-label"
            >
              <div className="relative mt-0.5">
                <input
                  type="checkbox"
                  checked={privacyChecked}
                  onChange={() => setPrivacyChecked(!privacyChecked)}
                  className="sr-only"
                  data-testid="privacy-checkbox"
                />
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 ${
                  privacyChecked
                    ? 'bg-sage border-sage'
                    : 'border-gray-300 group-hover:border-sage/50'
                }`}>
                  {privacyChecked && <Check size={14} className="text-white" strokeWidth={3} />}
                </div>
              </div>
              <span className="text-sm text-charcoal/80 leading-relaxed">
                I have read and agree to the{' '}
                <Link
                  to="/privacy"
                  target="_blank"
                  className="text-sage hover:text-sage-dark underline font-medium"
                  data-testid="privacy-link"
                >
                  Privacy Policy
                </Link>
              </span>
            </label>
          </div>

          {/* Acknowledgement note */}
          <p className="text-xs text-gray-soft leading-relaxed mb-6 bg-cream rounded-lg p-3">
            By accepting, you also acknowledge that these documents may be updated
            from time to time and that it is your responsibility to review any changes.
            Continued use of the service constitutes acceptance of updated terms.
          </p>

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleCancel}
              className="flex-1 py-2.5 rounded-lg text-sm font-medium text-gray-soft border border-beige hover:bg-cream transition-all duration-200"
              data-testid="terms-cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={handleAccept}
              disabled={!canAccept}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                canAccept
                  ? 'bg-sage text-white hover:bg-sage-dark hover:scale-[1.01] active:scale-[0.99]'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              data-testid="terms-accept-btn"
            >
              {submitting ? 'Accepting...' : 'Accept & Continue'}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-beige px-6 py-3 flex items-center justify-center gap-4 text-xs text-gray-soft">
          <Link to="/terms" target="_blank" className="flex items-center gap-1 hover:text-sage transition-colors">
            <FileText size={12} /> Terms
          </Link>
          <Link to="/privacy" target="_blank" className="flex items-center gap-1 hover:text-sage transition-colors">
            <Shield size={12} /> Privacy
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsAcceptanceModal;
